// Pure helpers + types for milestone form state. Same pattern as the
// insurance + deliverable variants. paymentAmount is modelled as
// `number | ""` because CurrencyInput writes 0 when blank-ish; we keep
// the form-state empty-string distinct from an explicit 0 to support
// "no payment tied to this milestone" cleanly on Create.

import type {
  CreateMilestoneInput,
  EvidenceLink,
  Milestone,
} from "../../../../types";

export interface MilestoneFormState {
  name: string;
  /** Raw text of the milestone-number field, e.g. "13" or "13a". Held as a
   *  string so partial/invalid typing round-trips; parsed + validated into
   *  `{ milestoneNumber, milestoneSuffix }` on submit. */
  milestoneNumberInput: string;
  plannedDate: string;
  deliveredAt: string;
  partiallyAcceptedAt: string;
  acceptedAt: string;
  paidAt: string;
  paymentAmount: number | "";
  description: string;
  acceptanceCriteria: string;
  /** SOW attachment. `null` is the explicit `(none)` selection; on
   *  Edit, transitioning a previously-attached milestone to `null`
   *  fires an explicit-null clear in the Update payload. On Create,
   *  `null` is omitted from the payload (helper drops it). */
  sowId: string | null;
  evidenceLinks: EvidenceLink[];
  notes: string;
}

export type MilestoneFieldErrors = Partial<
  Record<keyof MilestoneFormState | "form", string>
>;

// Milestone number = an integer backbone with an optional single trailing
// lowercase letter that slots a milestone between two existing numbers
// ("13a" sits after 13, before 14) without renumbering.
const MILESTONE_NUMBER_RE = /^(\d+)([a-z])?$/;

/** Parse the milestone-number text field into its number + optional single
 *  letter. Trims and lowercases first. Returns null on anything that isn't
 *  digits optionally followed by one a–z letter, or a number < 1. */
export function parseMilestoneNumberInput(
  raw: string,
): { milestoneNumber: number; milestoneSuffix?: string } | null {
  const m = MILESTONE_NUMBER_RE.exec(raw.trim().toLowerCase());
  if (!m) return null;
  const milestoneNumber = Number(m[1]);
  if (!Number.isInteger(milestoneNumber) || milestoneNumber < 1) return null;
  return { milestoneNumber, milestoneSuffix: m[2] || undefined };
}

/** Friendly validation message for the milestone-number field, or undefined
 *  when valid. Shared by the modal and inline-expand edit surfaces so they
 *  stay in lockstep. */
export function milestoneNumberInputError(input: string): string | undefined {
  return parseMilestoneNumberInput(input)
    ? undefined
    : "Enter a number, optionally followed by one letter (e.g. 13 or 13a)";
}

/** Render a milestone's identifier for display: number + optional suffix. */
export function formatMilestoneNumber(m: {
  milestoneNumber: number;
  milestoneSuffix?: string | null;
}): string {
  return `${m.milestoneNumber}${m.milestoneSuffix ?? ""}`;
}

export function buildEmptyMilestoneFormState(): MilestoneFormState {
  return {
    name: "",
    milestoneNumberInput: "1",
    plannedDate: "",
    deliveredAt: "",
    partiallyAcceptedAt: "",
    acceptedAt: "",
    paidAt: "",
    paymentAmount: "",
    description: "",
    acceptanceCriteria: "",
    sowId: null,
    evidenceLinks: [],
    notes: "",
  };
}

export function stateFromMilestone(item: Milestone): MilestoneFormState {
  return {
    name: item.name,
    milestoneNumberInput: formatMilestoneNumber(item),
    plannedDate: item.plannedDate,
    deliveredAt: item.deliveredAt ?? "",
    partiallyAcceptedAt: item.partiallyAcceptedAt ?? "",
    acceptedAt: item.acceptedAt ?? "",
    paidAt: item.paidAt ?? "",
    paymentAmount:
      typeof item.paymentAmount === "number" ? item.paymentAmount : "",
    description: item.description ?? "",
    acceptanceCriteria: item.acceptanceCriteria ?? "",
    sowId: item.sowId ?? null,
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
  };
}

/** Lifecycle field names in forward-ladder chronological order. */
export const LIFECYCLE_FIELDS = [
  "deliveredAt",
  "partiallyAcceptedAt",
  "acceptedAt",
  "paidAt",
] as const;

export type LifecycleField = (typeof LIFECYCLE_FIELDS)[number];

/**
 * Build an explicit-null diff for lifecycle fields that were cleared.
 * When the original item had a value and the current state has an empty
 * string for that field, emit `null` in the patch body so the backend
 * actually clears the timestamp rather than leaving it unchanged.
 */
export function buildLifecycleNullPatch(
  state: MilestoneFormState,
  source: Milestone,
): Partial<Record<LifecycleField, null>> {
  const patch: Partial<Record<LifecycleField, null>> = {};
  for (const field of LIFECYCLE_FIELDS) {
    if (source[field] && !state[field]) {
      patch[field] = null;
    }
  }
  return patch;
}

/**
 * Build the backend Create payload. Strips empty-string optionals and
 * coerces empty-string `paymentAmount` to undefined (omitted).
 *
 * Note: the milestone↔deliverable link is no longer carried on the
 * milestone Create payload — it lives on `deliverable.milestoneId` and
 * is set via the bulk-set endpoint after the milestone exists.
 */
export function toCreateMilestoneInput(
  state: MilestoneFormState,
  contractId: string,
): CreateMilestoneInput {
  const parsedNumber = parseMilestoneNumberInput(state.milestoneNumberInput);
  const base: CreateMilestoneInput = {
    contractId,
    name: state.name.trim(),
    // Validation (milestoneNumberInputError) gates submit, so parsedNumber is
    // non-null here; the `?? 0` keeps the type total without a non-null assert.
    milestoneNumber: parsedNumber?.milestoneNumber ?? 0,
    plannedDate: state.plannedDate,
  };
  if (parsedNumber?.milestoneSuffix) {
    base.milestoneSuffix = parsedNumber.milestoneSuffix;
  }
  if (state.deliveredAt) base.deliveredAt = state.deliveredAt;
  if (state.partiallyAcceptedAt)
    base.partiallyAcceptedAt = state.partiallyAcceptedAt;
  if (state.acceptedAt) base.acceptedAt = state.acceptedAt;
  if (state.paidAt) base.paidAt = state.paidAt;
  if (state.paymentAmount !== "" && Number.isFinite(state.paymentAmount)) {
    base.paymentAmount = state.paymentAmount as number;
  }
  if (state.description.trim()) base.description = state.description.trim();
  if (state.acceptanceCriteria.trim()) {
    base.acceptanceCriteria = state.acceptanceCriteria.trim();
  }
  if (state.sowId) base.sowId = state.sowId;
  if (state.evidenceLinks.length > 0) base.evidenceLinks = state.evidenceLinks;
  if (state.notes.trim()) base.notes = state.notes.trim();
  return base;
}

/**
 * Compute the `sowId` patch for a milestone Update payload. Mirrors
 * the deliverable variant: emit `null` only when source had a SOW and
 * state is now null (clear); emit the new id when changed; omit
 * otherwise (idempotent PUT).
 */
export function buildSowIdUpdatePatch(
  state: MilestoneFormState,
  source: Milestone,
): { sowId?: string | null } {
  const before = source.sowId ?? null;
  const after = state.sowId;
  if (before === after) return {};
  return { sowId: after };
}

/**
 * Compute the `milestoneSuffix` patch for an Update. Emits the new letter
 * when changed, `null` to clear (e.g. 13a → 13) when the source had a suffix
 * and the draft no longer does, and omits the field when unchanged (idempotent
 * PUT). Mirrors `buildSowIdUpdatePatch`; the backend routes `null` into
 * removeFields so the stored attribute is dropped.
 */
export function buildMilestoneSuffixUpdatePatch(
  state: MilestoneFormState,
  source: Milestone,
): { milestoneSuffix?: string | null } {
  const before = source.milestoneSuffix ?? undefined;
  const after = parseMilestoneNumberInput(
    state.milestoneNumberInput,
  )?.milestoneSuffix;
  if (before === after) return {};
  return { milestoneSuffix: after ?? null };
}

/**
 * Pick the next milestoneNumber for a Create flow. Defaults to 1 when
 * the contract has no milestones yet; otherwise returns max+1. The
 * value is editable (validation enforces integer ≥ 1) so a user
 * inserting between existing rows can override.
 */
export function nextMilestoneNumber(
  existing: readonly Milestone[],
): number {
  if (!existing.length) return 1;
  return Math.max(...existing.map((m) => m.milestoneNumber)) + 1;
}
